// Copyright (c) Ping. All rights reserved.

// Auth-flow state machine — discriminants match the Wave-3
// cross-platform naming contract (`/tmp/ping-cycle-16/_NAMING_CONTRACT-WAVE3.md`)
// verbatim. Never persist anything in this file's shape: the user's
// email / phone / code / password / display name lives only in the
// in-memory machine while the user is on a step (Hard rule 3 + Hard
// rule 5). A process exit returns the user to `welcome`.
//
// invite-flow Wave-3A: lifted verbatim from
// `apps/desktop/src/auth/authFlowState.ts` so both apps consume the
// same FSM contract. Web and desktop coordinators import the types
// from this shared module instead of maintaining parallel copies.

export type AuthMode = 'signIn' | 'register';

export type AuthMethod = 'email' | 'phone';

export type ContactValue = { kind: 'email'; value: string } | { kind: 'phone'; e164: string };

export type AuthFailureReason =
  | 'network'
  | 'invalidCredential'
  | 'codeMismatch'
  // BE returned `identity_mismatch` on /v1/auth/verify — this email
  // or phone is already bound to a different device identity. The
  // user lost their old device or local state was wiped. Recovery
  // path: Restore from backup OR Link via QR from a still-active
  // device (or, for dev, reset the server-side account row).
  | 'identityMismatch'
  // BE returned `account_not_found` on /v1/auth/verify with intent=signin —
  // the OTP was correct but no account exists for this email/phone. Keep the
  // user in the sign-in flow and steer them to sign-up rather than silently
  // creating an account.
  | 'accountNotFound'
  | 'passwordTooWeak'
  | 'phoneInvalid'
  | 'emailInvalid'
  | 'serverError'
  | 'rateLimited';

/**
 * The handoff value produced when the auth flow reaches `done`. The
 * coordinator never carries access tokens through this shape — the
 * access token is held in module memory by each app's network layer
 * (web: `core/network/interceptor`; desktop: `core/network/api`), and
 * the refresh token is held in IndexedDB (web) or Keychain (desktop).
 * `tokens.hasAccessToken: true` is a marker that authenticated requests
 * are now possible.
 */
export interface AuthenticatedUser {
  readonly tokens: { readonly hasAccessToken: true };
  readonly identity: {
    readonly accountId: string;
    readonly deviceId: string;
    readonly displayName: string;
  };
}

export type AuthFlowState =
  | { kind: 'welcome' }
  | { kind: 'chooseMethod' }
  | { kind: 'enteringEmail' }
  | { kind: 'enteringPhone' }
  | { kind: 'enteringCode'; method: AuthMethod; contact: ContactValue }
  | { kind: 'enteringPassword'; authMode: AuthMode }
  | { kind: 'enteringSaveLoginChoice' }
  | { kind: 'enteringDisplayName' }
  | { kind: 'acceptingPrivacy' }
  | { kind: 'importingContacts' }
  | { kind: 'findingCompany' }
  | { kind: 'done'; user: AuthenticatedUser };

// `AuthFailureReason` is still exported from this file because the
// toast-based failure surface in apps (`useAuthFlowCoordinator`'s
// `describeFailure` map) consumes it. The state machine no longer
// has a `failed` state — error feedback is delivered out-of-band via
// `pingToast.error(...)` and the user remains on the step that
// produced the error so they can retry inline.

export interface AuthFlowContext {
  readonly authMode: AuthMode;
  readonly challengeId: string | null;
  readonly displayName: string | null;
  readonly saveLogin: boolean | null;
}

export interface AuthFlowMachine {
  readonly state: AuthFlowState;
  readonly context: AuthFlowContext;
}

export type AuthFlowAction =
  | { type: 'welcomeChoseExisting' }
  | { type: 'welcomeChoseNew' }
  | { type: 'methodChoseEmail' }
  | { type: 'methodChosePhone' }
  | { type: 'emailValidated'; email: string }
  | { type: 'phoneValidated'; e164: string }
  | { type: 'codeRequested'; challengeId: string }
  | { type: 'codeVerified' }
  | { type: 'passwordSet' }
  | { type: 'saveLoginChoiceMade'; saveLogin: boolean }
  | { type: 'displayNameSet'; displayName: string }
  | { type: 'privacyAccepted' }
  | { type: 'importContactsCompleted' }
  | { type: 'importContactsSkipped' }
  | { type: 'findCompanyCompleted' }
  | { type: 'findCompanySkipped' }
  | { type: 'authenticated'; user: AuthenticatedUser }
  | { type: 'back' };

export const initialAuthFlowMachine: AuthFlowMachine = {
  state: { kind: 'welcome' },
  context: {
    authMode: 'signIn',
    challengeId: null,
    displayName: null,
    saveLogin: null,
  },
};
